import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { set } from 'lodash'; // npm install lodash if not already

import VideoEditor from '../../components/VideoEditor';
import FormFields from '../../components/editorForm/form/FormFields';
import { useLoader } from '../../context/LoaderContext';
import { useToast } from '../../context/ToastContext';
import api from '../../api/axios';
import '../../css/App.css';

import HeroBg from "../../../src/images/hero-bg.png";
import { getUser } from '../../api/auth';

// New imports
import { LAYOUT_CONFIG } from '../../api/layoutConfig';
import { validateData } from '../../components/validation/validateUtils';

export default function EditorComponent({ role }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { layout } = location.state || {};

    const { loaderStart, loaderClose } = useLoader();
    const { success, error } = useToast();

    // ---- State ----
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [selectedLayout, setSelectedLayout] = useState('');
    const [templateData, setTemplateData] = useState({}); // key: template_id, value: data object
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [campaigns, setCampaigns] = useState([]);

    const DESIGN_WIDTH = 1200;
    const DESIGN_HEIGHT = 750;
    const [scale, setScale] = useState(1);

    // ---- Fetch templates and campaigns ----
    useEffect(() => {
        const handleResize = () => {
            const outer = document.querySelector(".canvas-outer");
            if (!outer) return;
            const { width, height } = outer.getBoundingClientRect();
            const scaleX = width / DESIGN_WIDTH;
            const scaleY = height / DESIGN_HEIGHT;
            setScale(Math.min(scaleX, scaleY));
        };

        getTemplates();
        getCampaigns();
        handleResize();
        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Fetch templates from API
    const getTemplates = async () => {
        try {
            setLoading(true);
            const response = await api.get("/template/list");
            if (response?.data?.success) {
                const list = response.data.data.list;
                setTemplates(list);
                setLoading(false);

                // Select initial template
                let initialTemplate = null;
                if (layout) {
                    initialTemplate = list.find(t => t.template_layout === layout);
                }
                if (!initialTemplate && list.length > 0) {
                    initialTemplate = list[0];
                }
                if (initialTemplate) {
                    handleTemplateChange(initialTemplate.template_id, list);
                }
            } else {
                setLoading(false);
                setTemplates([]);
            }
        } catch (err) {
            setLoading(false);
            console.error('Error fetching templates:', err);
        }
    };

    // Fetch campaigns for the dropdown
    const getCampaigns = async () => {
        try {
            const response = await api.get('campaign/list');
            if (response?.data?.data) {
                const names = response.data.data.map(c => c.camp_name);
                setCampaigns(names);
            }
        } catch (err) {
            console.error('Failed to load campaigns', err);
        }
    };

    // ---- Helper: canvas background ----
    const getCanvasBackground = (canvas) => {
        const type = canvas?.backgroundType || 'solid';
        if (type === 'gradient') {
            const angle = Number.isFinite(Number(canvas?.gradientAngle)) ? Number(canvas.gradientAngle) : 135;
            const c1 = canvas?.gradientColor1 || canvas?.backgroundColor || '#e0e0e0';
            const c2 = canvas?.gradientColor2 || '#ffffff';
            return `linear-gradient(${angle}deg, ${c1}, ${c2})`;
        }
        return canvas?.backgroundColor || '#e0e0e0';
    };

    // ---- Template change handler ----
    const handleTemplateChange = (templateId, templateList) => {
        const template = templateList.find(t => t.template_id === templateId);
        if (!template) return;

        setSelectedTemplateId(templateId);
        setSelectedLayout(template.template_layout);
        setErrors({});

        // If no data exists for this template, initialise with layout defaults
        if (!templateData[templateId]) {
            const defaults = template?.template_content?.defaultValues || {};
            setTemplateData(prev => ({
                ...prev,
                [templateId]: defaults
            }));
        }
    };

    // ---- Form change: updates the currently selected template's data ----
    const handleFormChange = (fieldPath, value) => {
        if (!selectedTemplateId) return;
        setTemplateData(prev => {
            const newData = { ...prev };
            set(newData[selectedTemplateId], fieldPath, value);
            return newData;
        });
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[fieldPath];
            return newErrors;
        });
    };

    // ---- Validation ----
    const validateForm = () => {
        const template = templates.find(t => t.template_id === selectedTemplateId);
        if (!template) return {};
        const config = LAYOUT_CONFIG[template.template_layout];
        if (!config || !config.validation) return {};
        const data = templateData[selectedTemplateId] || {};
        return validateData(data, config.validation);
    };

    // ---- Save ----
    const handleSave = async () => {
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setErrors({});

        const userDetail = getUser();
        if (!userDetail) {
            error("Please login to generate video creatives.");
            return;
        }

        const template = templates.find(t => t.template_id === selectedTemplateId);
        if (!template) {
            error("No template selected.");
            return;
        }

        try {
            const dataToSave = {
                layout: template.template_layout,   // e.g., "layout3"
                data: templateData[selectedTemplateId] || {},
                timestamp: new Date().toISOString()
            };

            loaderStart();
            const response = await api.post('creative/generate-video', dataToSave);
            loaderClose();

            if (response.data && response.data.success) {
                success('Creative saved successfully!');
                setTimeout(() => navigate(`/${role}/creatives`), 1000);
            }
        } catch (err) {
            loaderClose();
            console.log(err, ">>>>>>>>>>>")
            error(err?.response?.data?.message || err?.data?.message || err?.message || "Something went wrong");
        }
    };

    // ---- Current data for rendering ----
    const currentData = templateData[selectedTemplateId] || {};

    return (
        <div className="app">
            <div className="app-header">
                <h2 className="premium-page-title">
                    Video Editor Canvas&nbsp;
                    <span className="text-xs text-gray-500 tracking-wide">
                        (Preview is a quick simulation. Final video may have minor visual differences.)
                    </span>
                </h2>
                <div className="header-controls">
                    <div className="layout-selector">
                        <label htmlFor="layout-dropdown">Template:</label>
                        <div className="layout-dropdown-wrapper">
                            <select
                                id="layout-dropdown"
                                value={selectedTemplateId || ''}
                                onChange={(e) => handleTemplateChange(e.target.value, templates)}
                                className="layout-dropdown"
                            >
                                {templates.map(t => (
                                    <option key={t.template_id} value={t.template_id}>
                                        {t.template_name}
                                    </option>
                                ))}
                            </select>
                            <span className="layout-arrow"><ChevronDown /></span>
                        </div>
                    </div>
                </div>
            </div>

            {selectedLayout === "contact" ? (
                <div className="flex items-center justify-center min-h-[750px] p-8">
                    <div className="relative overflow-hidden max-w-xl w-full rounded-3xl shadow-2xl">
                        <div className="absolute inset-0">
                            <img src={HeroBg} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="absolute inset-0" />
                        <div className="relative z-10 p-10 text-center backdrop-blur-[2px]">
                            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center shadow-lg"
                                style={{
                                    background: "linear-gradient(90deg, #2f7cf7 0%, #4f60f3 45%, #6c3df0 100%)"
                                }}>
                                <span className="text-4xl">✨</span>
                            </div>
                            <h2 className="text-4xl font-bold text-black mb-4">Premium Video Creative</h2>
                            <p className="text-gray-700 text-lg leading-8 mb-8">
                                Contact our team to unlock this exclusive creative layout.
                            </p>
                            <button
                                onClick={() => navigate("/contact")}
                                style={{
                                    background: "linear-gradient(90deg, #2f7cf7 0%, #4f60f3 45%, #6c3df0 100%)"
                                }}
                                className="px-7 py-3 rounded-xl transition-all duration-300 text-white font-semibold shadow-xl"
                            >
                                Contact Us
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="app-content">
                    <div className="canvas-outer">
                        <div
                            className="canvas-container"
                            style={{
                                transform: `scale(${scale})`,
                                transformOrigin: "center center",
                                background: getCanvasBackground(currentData?.canvas)
                            }}
                        >
                            <VideoEditor
                                layout={selectedLayout}
                                data={currentData}
                            />
                        </div>
                    </div>

                    <div className="form-container">
                        <FormFields
                            layout={selectedLayout}
                            data={currentData}
                            onChange={handleFormChange}   // now (fieldPath, value)
                            errors={errors}
                            onSave={handleSave}
                            campaigns={campaigns}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}